/**
 * Picking files, and the banner that reports them.
 *
 * The banner lives in the app shell, outside every route, which is the whole
 * point: an upload started in one folder keeps running and keeps reporting
 * while you browse somewhere else, open a dialog or search. Closing the picker
 * only hides the picker.
 */
import { useRef, useState } from 'react';
import { FolderUp, Upload, X } from 'lucide-react';
import { useUploads } from '../lib/uploads';
import { formatBytes } from '../lib/format';
import { Button, Field, Modal, TextArea, useConfirm } from './ui';

/**
 * Folder picking uses `webkitdirectory`, which iOS Safari and Chrome on
 * Android do not implement, so the control is feature-detected and hidden
 * where it would silently do nothing. Single and multi-file upload work
 * everywhere.
 */
function supportsDirectoryPicker(): boolean {
  if (typeof document === 'undefined') return false;
  return 'webkitdirectory' in document.createElement('input');
}

export function UploadDialog({
  open,
  onOpenChange,
  folderId,
  folderName,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  folderId: string | null;
  folderName: string;
}) {
  const { addFiles } = useUploads();
  const [description, setDescription] = useState('');
  const fileInput = useRef<HTMLInputElement>(null);
  const folderInput = useRef<HTMLInputElement>(null);
  const canPickFolder = supportsDirectoryPicker();

  const accept = (list: FileList | null) => {
    if (!list || list.length === 0) return;
    addFiles(Array.from(list), { folderId, description });
    setDescription('');
    onOpenChange(false);
  };

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="Upload"
      description={`Into ${folderName}`}
      footer={<Button onClick={() => onOpenChange(false)}>Close</Button>}
    >
      <Field label="Description (applied to everything in this upload)">
        <TextArea
          value={description}
          onChange={(event) => setDescription(event.target.value)}
          placeholder="Optional"
        />
      </Field>

      <div className="grid gap-3 sm:grid-cols-2">
        <button
          type="button"
          onClick={() => fileInput.current?.click()}
          className="flex flex-col items-center gap-2 rounded-xl border border-dashed border-line px-4 py-8 text-sm hover:bg-surface-2"
        >
          <Upload className="size-6 text-brand" aria-hidden />
          <span className="font-medium">Choose files</span>
          <span className="text-xs text-ink-dim">One or many</span>
        </button>

        {canPickFolder ? (
          <button
            type="button"
            onClick={() => folderInput.current?.click()}
            className="flex flex-col items-center gap-2 rounded-xl border border-dashed border-line px-4 py-8 text-sm hover:bg-surface-2"
          >
            <FolderUp className="size-6 text-brand" aria-hidden />
            <span className="font-medium">Choose a folder</span>
            <span className="text-xs text-ink-dim">Keeps its structure</span>
          </button>
        ) : null}
      </div>

      <p className="mt-4 text-xs text-ink-dim">
        Uploads keep running while you carry on using the site, and pick up where they left off if
        the connection drops.
      </p>

      <input
        ref={fileInput}
        type="file"
        multiple
        hidden
        onChange={(event) => {
          accept(event.target.files);
          event.target.value = '';
        }}
      />
      {canPickFolder ? (
        <input
          ref={folderInput}
          type="file"
          multiple
          hidden
          // Not a React prop, hence the cast; it is what makes the picker
          // offer a directory instead of files.
          {...({ webkitdirectory: '', directory: '' } as Record<string, string>)}
          onChange={(event) => {
            accept(event.target.files);
            event.target.value = '';
          }}
        />
      ) : null}
    </Modal>
  );
}

/** The pinned progress banner. Rendered by the shell, never by a route. */
export function UploadBanner() {
  const { state, cancelAll } = useUploads();
  const confirm = useConfirm();

  if (state.total === 0) return null;

  const percent =
    state.bytesTotal > 0 ? Math.round((state.bytesUploaded / state.bytesTotal) * 100) : 0;
  const done = !state.active;

  const onCancel = async () => {
    const ok = await confirm({
      title: 'Stop the upload?',
      body: 'Files already finished stay. The rest are cancelled.',
      confirmLabel: 'Stop uploading',
    });
    if (ok) cancelAll();
  };

  return (
    <div className="border-t border-line bg-surface px-4 py-2.5 pb-[max(0.625rem,env(safe-area-inset-bottom))]">
      <div className="mx-auto flex max-w-5xl items-center gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline justify-between gap-3">
            <p className="truncate text-sm font-medium">
              {done
                ? `${state.complete} of ${state.total} uploaded`
                : (state.currentName ?? `Uploading ${state.total} file${state.total === 1 ? '' : 's'}`)}
            </p>
            <p className="shrink-0 text-xs text-ink-dim tabular-nums">
              {done
                ? state.failed > 0
                  ? `${state.failed} failed`
                  : 'Done'
                : `${formatBytes(state.bytesUploaded)} / ${formatBytes(state.bytesTotal)}`}
            </p>
          </div>
          <div
            className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-surface-2"
            role="progressbar"
            aria-valuenow={percent}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label="Upload progress"
          >
            <div
              className="h-full rounded-full bg-brand transition-[width] duration-300"
              style={{ width: `${percent}%` }}
            />
          </div>
          <p className="mt-1 text-xs text-ink-dim">
            {state.complete} of {state.total} done
            {state.failed > 0 ? `, ${state.failed} failed` : ''}
          </p>
        </div>

        {state.active ? (
          <Button icon variant="ghost" aria-label="Cancel upload" onClick={onCancel}>
            <X className="size-5" />
          </Button>
        ) : null}
      </div>
    </div>
  );
}
